import React, { useState, useEffect } from 'react';
import { BASE_URL } from './Config';
import config from "./common/Config";
import { toast } from 'sonner';
import {
	Box,
	Typography,
	Paper,
	TextField,
	InputAdornment,
	TablePagination,
	IconButton,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Skeleton,
	Link as MuiLink,
	Chip,
	Grid2,
} from '@mui/material';
import { Iconify } from './Minimal/src/components/iconify/iconify';
import "./common/commonStyles.css";

const columns = [
	{ id: "id", label: "#" },
	{ id: "date", label: "Date" },
	{ id: "manifest_id", label: "Manifest Number" },
	{ id: "trip_id", label: "Trip ID" },
	{ id: "driver", label: "Driver" },
	{ id: "route_number", label: "Route" },
	{ id: "sales_person", label: "Sales Person" },
	{ id: "status", label: "Status" },
	{ id: "action", label: "Action" },
];

const ManifestList = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [totalRecords, setTotalRecords] = useState(0);
	const [searchInputValue, setSearchInputValue] = useState('');
	const [searchTerm, setSearchTerm] = useState('');
	const [menuAnchor, setMenuAnchor] = useState({ el: null, manifestId: null });
	const accessToken = localStorage.getItem('accessToken');
	const token = localStorage.getItem("token");

	const fetchData = async () => {
		if (!accessToken || !token) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}

		setLoading(true);
		const formData = new FormData();
		formData.append('draw', page + 1);
		formData.append('start', page * rowsPerPage);
		formData.append('length', rowsPerPage);
		formData.append('search[value]', searchTerm);

		try {
			const response = await fetch(`${config.API_URL}/manifest/list`, {
				method: 'POST',
				headers: { authorization: `Bearer ${token}` },
				body: formData
			});

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const json = await response.json();
			const manifests = Array.isArray(json.data) ? json.data : [];

			const manifestsWithSrNo = manifests.map((manifest, index) => ({
				...manifest,
				sr_no: page * rowsPerPage + index + 1
			}));

			setData(manifestsWithSrNo);
			setTotalRecords(json.recordsTotal || json.recordsFiltered || manifests.length);
		} catch (error) {
			setData([]);
			setTotalRecords(0);
			toast.error('Failed to load manifests');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (accessToken === null) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}
		fetchData();
	}, [page, rowsPerPage, searchTerm, accessToken]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchInputValue(value);
		setSearchTerm(value);
		setPage(0);
	};

	const handleSearchKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			setPage(0);
		}
	};

	const handleMenuOpen = (event, manifestId) => {
		event.stopPropagation();
		setMenuAnchor({ el: event.currentTarget, manifestId });
	};

	const handleMenuClose = (event) => {
		if (event) {
			event.stopPropagation();
		}
		setMenuAnchor({ el: null, manifestId: null });
	};

	const handleMenuAction = (action, manifestId) => {
		handleMenuClose();
		if (action === 'edit' && manifestId) {
			window.location.href = `${BASE_URL}/manifest_list/mark_delivery/${manifestId}`;
		} else if (action === 'print' && manifestId) {
			window.location.href = `${BASE_URL}/manifest_list/ManifestPrint/${manifestId}`;
		}
	};

	return (
		<Box>
			<Box className="page-container">
				<Paper className="paper-style">
					<Box sx={{ px: 2, py: 1.2 }}>
						<Grid2 container spacing={2} display="flex" justifyContent="space-between">
							<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
								<Typography className="page-title">
									Manifest List
								</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6, md: 4 }} display="flex" justifyContent="flex-end">
								<TextField
									className="input-border"
									fullWidth
									size="small"
									placeholder="Search manifests..."
									value={searchInputValue}
									onChange={handleSearchChange}
									onKeyDown={handleSearchKeyDown}
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Iconify icon="eva:search-outline"/>
												</InputAdornment>
											)
										}
									}}
								
								/>
							</Grid2>
						</Grid2>
					</Box>
					<TableContainer sx={{ width: "100%" }}>
						<Table
							size="medium"
							className="table-cell-border"
							sx={{
								tableLayout: "auto",
							}}
						>
							<TableHead>
								<TableRow className="table-cell-header">
									{columns.map((column) => (
										<TableCell
										    sx={{px: 1}}
											key={column.id}
											align={column.id === "id" || column.id === "action" ? "center" : "left"}
										
										>
											{column.label}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody
							>
								{loading
									? Array.from({ length: rowsPerPage }).map((_, index) => (
										<TableRow key={`skeleton-${index}`}>
											{columns.map((column) => (
												<TableCell
													key={`${column.id}-${index}`}
													align={column.id === "id" || column.id === "action" ? "center" : "left"}
													sx={{
														py: 1,
														fontSize: "0.75rem",
													}}
												>
													<Skeleton variant="text" />
												</TableCell>
											))}
										</TableRow>
									))
									: data.length > 0
										? data.map((row) => (
											<TableRow
												key={row.id || row.manifest_id}
												hover
												className="table-cell-style"
											
											>
												<TableCell align="center" >
													{row.sr_no || row.id}
												</TableCell>
												<TableCell >
													{row.date || ''}
												</TableCell>
												<TableCell >
													{row.manifest_id ? (
														<MuiLink
															href={`${BASE_URL}/manifest_list/ManifestPrint/${row.manifest_id}`}
															target="_blank"
															rel="noopener noreferrer"
															sx={{
																color: "inherit",
																textDecoration: "underline !important",
																textDecorationColor: "currentColor",
																textUnderlineOffset: "2px",
																"&:hover": {
																	textDecoration: "underline !important",
																	textDecorationColor: "currentColor"
																}
															}}
														>
															{row.manifest_id}
														</MuiLink>
													) : '-'}
												</TableCell>
												<TableCell >
													{row.trip_id || ''}
												</TableCell>
												<TableCell >
													{row.driver || ''}
												</TableCell>
												<TableCell >
													{row.route_number || ''}
												</TableCell>
												<TableCell >
													{row.sales_person || ''}
												</TableCell>
												<TableCell >
													{row.status ? (
														<Chip
															label={row.status}
															size="small"
															color="#F8991D"
															sx={{
																fontWeight: 500,
																fontSize: '0.7rem',
																height: 24,
																backgroundColor: "#F8991D",
																color: "#fff",
															}}
														/>
													) : ''}
												</TableCell>
												<TableCell align="center" >
													{!row.trip_id && row.manifest_id ? (
														<IconButton
															size="small"
															onClick={(e) => handleMenuOpen(e, row.manifest_id)}
															sx={{
																color: "#637381",
																"&:hover": {
																	backgroundColor: "rgba(0, 0, 0, 0.04)"
																}
															}}
														>
															<Iconify icon="eva:more-vertical-fill" width={20} height={20} />
														</IconButton>
													) : ''}
												</TableCell>
											</TableRow>
										))
										: (
											<TableRow>
												<TableCell colSpan={columns.length} sx={{ py: 4 }}>
													<Typography align="center">
														No records found.
													</Typography>
												</TableCell>
											</TableRow>
										)}
							</TableBody>
						</Table>
					</TableContainer>
					{data.length > 0 && (
						<Box display="flex" justifyContent="flex-end" alignItems="center">
							<TablePagination
								component="div"
								count={totalRecords}
								page={page}
								onPageChange={handleChangePage}
								rowsPerPage={rowsPerPage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								rowsPerPageOptions={[10, 25, 50, 100]}
								className="pagination-styles"
							/>
						</Box>
					)}
				</Paper>
			</Box>

			<Menu
				anchorEl={menuAnchor.el}
				open={Boolean(menuAnchor.el)}
				onClose={handleMenuClose}
				onClick={(e) => e.stopPropagation()}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				PaperProps={{
					onClick: (e) => e.stopPropagation(),
					sx: {
						minWidth: 140,
						borderRadius: 2,
						boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
						mt: 1,
					},
				}}
			>
				<MenuItem onClick={() => handleMenuAction('edit', menuAnchor.manifestId)}>
					<ListItemIcon>
						<Iconify icon="eva:edit-fill" width={20} height={20} />
					</ListItemIcon>
					<ListItemText>Edit</ListItemText>
				</MenuItem>
				<MenuItem onClick={() => handleMenuAction('print', menuAnchor.manifestId)}>
					<ListItemIcon>
						<Iconify icon="eva:printer-fill" width={20} height={20} />
					</ListItemIcon>
					<ListItemText>Print</ListItemText>
				</MenuItem>
			</Menu>

		</Box>
	);
};

export default ManifestList;
